import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance } from 'vue';
import { LogisticsTopBar, VehiclesSection, MontageTeamsSection, CrewFormModal } from '@/modules/Logistics/components';
import type { CrewFormSubmitData } from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import {
    VEHICLES_LIST_TABLE_COLUMNS,
    MONTAGE_TEAMS_TABLE_COLUMNS,
    MONTAGE_TEAMS_FORM_COLUMNS,
    VEHICLE_TYPE_OPTIONS,
    VEHICLE_PURPOSE_OPTIONS,
} from '@/modules/Logistics/helpers/LogisticsTableHelper';
import Vehicle from '@/modules/Logistics/api/models/Vehicle';
import MontageTeam from '@/modules/Logistics/api/models/MontageTeam';
import VehicleRepository from '@/modules/Logistics/api/repositories/VehicleRepository';
import MontageTeamRepository from '@/modules/Logistics/api/repositories/MontageTeamRepository';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';


export default /*#__PURE__*/_defineComponent({
  __name: 'Crews',
  setup(__props) {

const { proxy } = getCurrentInstance()!;
const loadingOverlay = new LoadingOverlayHelper(proxy, {});

const activeTab = ref<'vehicles' | 'teams'>('vehicles');
const modalVisible = ref(false);
const selectedRecord = ref<Record<string, any> | null>(null);
const vehiclesSelectedRowKeys = ref<string[]>([]);
const teamsSelectedRowKeys = ref<string[]>([]);

const vehiclesData = computed(() =>
    VehicleRepository.getAll().map((vehicle) => ({
        ...vehicle,
        purpose: proxy.$t(mapVehiclePurposeToLabel(vehicle.purpose)) as string,
        type: proxy.$t(mapVehicleTypeToLabel(vehicle.type)) as string,
    })),
);

const montageTeamsData = computed(() => {
    return MontageTeamRepository.getAll().map((team) => ({
        ...team,
        vehicleRegistration: team.vehicle?.registration || '',
    }));
});

function mapVehicleTypeToLabel(type: string): string {
    const option = VEHICLE_TYPE_OPTIONS.find((opt) => opt.value === type);
    return option ? option.label : type;
}

function mapVehiclePurposeToLabel(purpose: string): string {
    const option = VEHICLE_PURPOSE_OPTIONS.find((opt) => opt.value === purpose);
    return option ? option.label : purpose;
}

const vehicleOptions = computed(() => {
    const currentVehicleId = selectedRecord.value?.vehicleId;
    return VehicleRepository.getAll()
        .map((vehicle) => ({
            value: vehicle.id,
            label: vehicle.registration,
        }))
        .filter((vehicle) => {
            // Include currently selected vehicle for this team
            if (vehicle.value === currentVehicleId) return true;
            // Exclude vehicles assigned to other montage teams
            const isAssigned = MontageTeamRepository.getAll().some((team) => team.vehicleId === vehicle.value);
            return !isAssigned;
        });
});

const formColumns = computed(() =>
    activeTab.value === 'vehicles' ? VEHICLES_LIST_TABLE_COLUMNS : MONTAGE_TEAMS_FORM_COLUMNS,
);

(async () => {
    fetchInitialTableData();
})();

async function fetchInitialTableData() {
    loadingOverlay.start();
    try {
        await Promise.all([Vehicle.fetchAll(), MontageTeam.fetchAll()]);
    } catch (error) {
        console.error('Failed to fetch data:', error);
    } finally {
        loadingOverlay.stop();
    }
}

function handleRowClick(record: Record<string, any>, section: string) {
    openCrewDetailsModal(record, section, true);
}

function openCrewDetailsModal(record?: Record<string, any>, section?: string, isEditMode = false) {
    selectedRecord.value = record ?? null;
    initFormData(isEditMode);
    modalVisible.value = true;
}

function initFormData(isEditMode: boolean) {
    const data: Record<string, any> = {};
    const columns = activeTab.value === 'vehicles' ? VEHICLES_LIST_TABLE_COLUMNS : MONTAGE_TEAMS_FORM_COLUMNS;

    columns.forEach((col) => {
        if (col.type && selectedRecord.value && isEditMode) {
            const value = selectedRecord.value[col.dataIndex];
            if (col.type === 'boolean') {
                data[col.dataIndex] = value === true;
            } else {
                data[col.dataIndex] = value != null ? String(value) : '';
            }
        } else if (col.type) {
            data[col.dataIndex] = col.type === 'boolean' ? false : '';
        }
    });

    console.log(selectedRecord.value);

    initialFormData.value = data;
}

const initialFormData = ref<Record<string, any>>({});

function closeModal() {
    modalVisible.value = false;
    selectedRecord.value = null;
}

async function handleCreate(data: CrewFormSubmitData) {
    loadingOverlay.start();
    try {
        const formData = data.formData;
        if (activeTab.value === 'vehicles') {
            await Vehicle.createNew({
                registration: formData.registration || '',
                type: formData.type || '',
                purpose: formData.purpose || '',
                driver: formData.driver || '',
            });
        } else {
            await MontageTeam.createNew({
                name: formData.name || '',
                lastName: formData.lastName || '',
                driver: formData.driver === true,
                teamLeader: formData.teamLeader === true,
                installer: formData.installer === true,
                vehicleId: formData.vehicleId,
            });
            // Update vehicle driver with montage team member name
            if (formData.vehicleId && formData.driver === true) {
                await Vehicle.updateExisting(formData.vehicleId, {
                    driver: `${formData.name || ''} ${formData.lastName || ''}`.trim(),
                });
            }
        }
        fetchInitialTableData();
        closeModal();
    } catch (error) {
        console.error('Failed to create:', error);
    } finally {
        loadingOverlay.stop();
    }
}

async function handleUpdate(data: CrewFormSubmitData) {
    loadingOverlay.start();
    try {
        if (!selectedRecord.value?.id) return;

        const formData = data.formData;
        if (activeTab.value === 'vehicles') {
            await Vehicle.updateExisting(selectedRecord.value.id, {
                registration: formData.registration,
                type: formData.type,
                purpose: formData.purpose,
            });
        } else {
            const oldVehicleId = selectedRecord.value.vehicleId;
            const newVehicleId = formData.vehicleId;
            console.log(newVehicleId);

            await MontageTeam.updateExisting(selectedRecord.value.id, {
                name: formData.name,
                lastName: formData.lastName,
                driver: formData.driver === true,
                teamLeader: formData.teamLeader === true,
                installer: formData.installer === true,
                vehicleId: formData.vehicleId,
            });

            // Clear driver on old vehicle if vehicle changed
            if (oldVehicleId && oldVehicleId !== newVehicleId) {
                await Vehicle.updateExisting(oldVehicleId, { driver: '' });
            }

            // Update vehicle driver based on driver flag
            if (newVehicleId) {
                if (formData.driver === true) {
                    await Vehicle.updateExisting(newVehicleId, {
                        driver: `${formData.name || ''} ${formData.lastName || ''}`.trim(),
                    });
                } else if (oldVehicleId === newVehicleId) {
                    // Same vehicle but driver unchecked - clear it
                    await Vehicle.updateExisting(newVehicleId, { driver: '' });
                }
            }
        }
        fetchInitialTableData();
        closeModal();
    } catch (error) {
        console.error('Failed to update:', error);
    } finally {
        loadingOverlay.stop();
    }
}

async function handleDeleteRows(ids: string[], section: string) {
    loadingOverlay.start();
    try {
        if (section === 'vehicles') {
            await Promise.all(ids.map((id) => Vehicle.deleteById(id)));
            vehiclesSelectedRowKeys.value = [];
        } else {
            await Promise.all(ids.map((id) => MontageTeam.deleteById(id)));
            teamsSelectedRowKeys.value = [];
        }
    } catch (error) {
        console.error('Failed to delete:', error);
    } finally {
        loadingOverlay.stop();
    }
}

return { __sfc: true,proxy, loadingOverlay, activeTab, modalVisible, selectedRecord, vehiclesSelectedRowKeys, teamsSelectedRowKeys, vehiclesData, montageTeamsData, mapVehicleTypeToLabel, mapVehiclePurposeToLabel, vehicleOptions, formColumns, fetchInitialTableData, handleRowClick, openCrewDetailsModal, initFormData, initialFormData, closeModal, handleCreate, handleUpdate, handleDeleteRows, LogisticsTopBar, VehiclesSection, MontageTeamsSection, CrewFormModal, VEHICLES_LIST_TABLE_COLUMNS, MONTAGE_TEAMS_TABLE_COLUMNS }
}

})